import { formatarAteDuasCasas, formatarDia } from '@/lib/formato';

import type { AntesDaCorrecaoDaApi, ApontamentoDaApi, TurnoEstornadoDaApi } from './api';
import { medidorDo } from './medidor';

/**
 * A LISTA DE TURNOS DA R06 (p.69): o estornado fica oculto atrás da chave
 * "Mostrar estornados · N"; ligada, ele volta à POSIÇÃO ORIGINAL, riscado, em
 * cinza e com o motivo. O corrigido ganha a etiqueta "Corrigido" e, em segunda
 * linha, a mudança: "Correção: horímetro final 4.290 → 4.296".
 *
 * Funções puras, para o teste alcançar: a tela só desenha.
 */

/** Uma linha da tabela: o turno que vale, ou o estornado que a chave trouxe de volta. */
export type LinhaDeTurno =
  | { readonly tipo: 'vigente'; readonly id: string; readonly turno: ApontamentoDaApi }
  | { readonly tipo: 'estornado'; readonly id: string; readonly turno: TurnoEstornadoDaApi };

/**
 * A ordem da lista é a do servidor (dia mais recente primeiro, código da
 * máquina, turno). O estornado entra no lugar onde estava quando valia.
 */
function comparar(a: { dia: string; equipamento: string; turno: string }, b: { dia: string; equipamento: string; turno: string }): number {
  if (a.dia !== b.dia) return a.dia < b.dia ? 1 : -1;
  if (a.equipamento !== b.equipamento) return a.equipamento < b.equipamento ? -1 : 1;
  if (a.turno !== b.turno) return a.turno < b.turno ? -1 : 1;
  return 0;
}

export function linhasDosTurnos(
  vigentes: readonly ApontamentoDaApi[],
  estornados: readonly TurnoEstornadoDaApi[],
  mostrarEstornados: boolean,
): LinhaDeTurno[] {
  const linhas: LinhaDeTurno[] = vigentes.map((t) => ({ tipo: 'vigente', id: t.id, turno: t }));
  if (!mostrarEstornados || estornados.length === 0) return linhas;
  const todas = [...linhas, ...estornados.map((t): LinhaDeTurno => ({ tipo: 'estornado', id: t.id, turno: t }))];
  /* `sort` é estável: no empate (o turno certo e o errado no mesmo dia, máquina
     e turno), o que vale vem antes, e o estornado logo abaixo dele. */
  return todas.sort((x, y) => comparar(x.turno, y.turno));
}

const horimetro = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '—' : formatarAteDuasCasas(Number(v)));
const horas = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? '—' : `${formatarAteDuasCasas(Number(v))} h`);

/**
 * O que mudou entre o turno estornado e o que o corrigiu, campo a campo, na
 * fala do campo: "horímetro final 4.290 → 4.296".
 */
export function mudancasDaCorrecao(antes: AntesDaCorrecaoDaApi, depois: ApontamentoDaApi): string[] {
  const mudou: string[] = [];
  const num = (v: number | string | null | undefined) => (v === null || v === undefined || v === '' ? null : Number(v));
  /* A mesma máquina dos dois lados: o medidor do turno que vale diz o nome (0113). */
  const medidor = medidorDo(depois.tipoMedidor).noMeio;
  if (antes.dia !== depois.dia) mudou.push(`dia ${formatarDia(antes.dia)} → ${formatarDia(depois.dia)}`);
  if (antes.turno !== depois.turno) mudou.push(`turno ${antes.turno} → ${depois.turno}`);
  if (num(antes.horimetroInicial) !== num(depois.horimetroInicial)) {
    mudou.push(`${medidor} inicial ${horimetro(antes.horimetroInicial)} → ${horimetro(depois.horimetroInicial)}`);
  }
  if (num(antes.horimetroFinal) !== num(depois.horimetroFinal)) {
    mudou.push(`${medidor} final ${horimetro(antes.horimetroFinal)} → ${horimetro(depois.horimetroFinal)}`);
  }
  if (num(antes.horasEspera) !== num(depois.horasEspera)) {
    mudou.push(`espera ${horas(antes.horasEspera)} → ${horas(depois.horasEspera)}`);
  }
  if ((antes.causaCodigo ?? null) !== (depois.causaCodigo ?? null)) {
    mudou.push(`causa ${antes.causaCodigo ?? '—'} → ${depois.causaCodigo ?? '—'}`);
  }
  /* As horas apontadas mudam junto com o horímetro; só ditas quando foram a única mudança. */
  if (mudou.length === 0 && num(antes.horasApontadas) !== num(depois.horasApontadas)) {
    mudou.push(`horas ${horas(antes.horasApontadas)} → ${horas(depois.horasApontadas)}`);
  }
  return mudou;
}

/** A segunda linha do corrigido. Sem os valores de antes (servidor anterior à 0094), o motivo. */
export function textoDaCorrecao(t: ApontamentoDaApi): string | null {
  if (!t.corrigeId) return null;
  if (t.antesDaCorrecao) {
    const mudou = mudancasDaCorrecao(t.antesDaCorrecao, t);
    if (mudou.length > 0) return `Correção: ${mudou.join(' · ')}`;
  }
  return `Correção: ${t.motivoDaCorrecao ?? 'turno anterior estornado'}`;
}

/** A segunda linha do estornado: o motivo dele. */
export function textoDoEstorno(t: TurnoEstornadoDaApi): string {
  return `Estorno: ${t.motivo ?? 'sem motivo registrado'}`;
}

/** O rótulo da chave: "Mostrar estornados · 1". */
export function rotuloDaChaveDeEstornados(n: number): string {
  return `Mostrar estornados · ${String(n)}`;
}
